import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import FormateurPageShell from './FormateurPageShell';
import LucideSymbolIcon from '../icons/LucideSymbolIcon';
import { authService } from '../../services/authService';

type FormateurUser = {
  telephone?: string;
  profil?: { prenom?: string; nom?: string };
};

interface FormateurChangePasswordPanelProps {
  user: FormateurUser;
  /** Flux invitation : remplacement du code provisoire à 6 chiffres. */
  onboarding?: boolean;
  onPasswordChanged?: () => void | Promise<void>;
  homePath?: string;
  profilPath?: string;
}

export default function FormateurChangePasswordPanel({
  user,
  onboarding = false,
  onPasswordChanged,
  homePath = '/formateur',
  profilPath = '/formateur/profil',
}: FormateurChangePasswordPanelProps) {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    ancien: '',
    nouveau: '',
    confirm: '',
  });
  const [toast, setToast] = useState('');
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const displayName = useMemo(() => {
    const full = [user.profil?.prenom, user.profil?.nom].filter(Boolean).join(' ');
    return full || user.telephone || 'Formateur';
  }, [user.profil?.nom, user.profil?.prenom, user.telephone]);

  const handleSubmit = async () => {
    if (isSaving) return;
    setError('');
    setToast('');
    setIsSaving(true);
    try {
      await authService.changePassword({
        ancienMotdepasse: form.ancien,
        nouveauMotdepasse: form.nouveau,
        confirmMotdepasse: form.confirm,
      });
      setToast('Mot de passe mis à jour avec succès.');
      await onPasswordChanged?.();
      navigate(homePath);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Échec de la mise à jour');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <FormateurPageShell
      title="Mot de passe personnel"
      description={
        onboarding
          ? 'Dernière étape : remplacez le code à 6 chiffres reçu par e-mail par un mot de passe personnel sécurisé.'
          : 'Modifiez votre mot de passe de connexion à tout moment.'
      }
      sideCard={{
        label: 'Compte formateur',
        value: 'Sécurisé',
        helper: displayName,
        sideLinks: [
          { label: 'Mon profil', to: profilPath, icon: 'person' },
          { label: 'Mes sessions', to: '/formateur/sessions', icon: 'event' },
          { label: 'Rémunération', to: '/formateur/remuneration', icon: 'payments' },
        ],
      }}
      primaryAction={
        onboarding ? (
          <span className="inline-flex items-center gap-2 rounded-lg bg-white/20 px-3 py-1.5 text-xs font-bold text-white backdrop-blur-md">
            <LucideSymbolIcon name="lock" tone="inherit" className="text-white" size={14} />
            Étape 2 sur 2 — mot de passe
          </span>
        ) : undefined
      }
    >
      {onboarding ? (
        <div className="rounded-2xl border border-amber-200/80 bg-amber-50 px-4 py-3 text-sm text-amber-950 shadow-sm">
          <p className="font-bold">Sécurisez votre accès</p>
          <p className="mt-1 text-amber-900/90">
            Utilisez le code provisoire reçu par e-mail comme « mot de passe actuel ». Choisissez ensuite un mot de
            passe d&apos;au moins 8 caractères (pas uniquement 6 chiffres).
          </p>
        </div>
      ) : null}

      <article className="w-full rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-6 shadow-sm">
        <h2 className="text-sm font-black uppercase tracking-wider text-on-surface-variant">
          Changer le mot de passe
        </h2>
        <p className="mt-1 text-sm text-on-surface-variant">
          {onboarding
            ? 'Le code à 6 chiffres ne sera plus accepté après cette étape.'
            : 'Saisissez votre mot de passe actuel puis le nouveau.'}
        </p>

        <div className="mt-5 grid w-full grid-cols-1 gap-4 md:grid-cols-3">
          <label className="text-sm">
            <span className="mb-1 block text-on-surface-variant">
              {onboarding ? 'Code provisoire (e-mail)' : 'Mot de passe actuel'}
            </span>
            <input
              type="password"
              className="academia-field w-full"
              placeholder={onboarding ? 'Code à 6 chiffres' : 'Mot de passe actuel'}
              value={form.ancien}
              onChange={(e) => setForm((f) => ({ ...f, ancien: e.target.value }))}
              autoComplete="current-password"
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-on-surface-variant">Nouveau mot de passe</span>
            <input
              type="password"
              className="academia-field w-full"
              placeholder="8 caractères minimum"
              value={form.nouveau}
              onChange={(e) => setForm((f) => ({ ...f, nouveau: e.target.value }))}
              autoComplete="new-password"
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-on-surface-variant">Confirmer le nouveau mot de passe</span>
            <input
              type="password"
              className="academia-field w-full"
              placeholder="Répétez le nouveau mot de passe"
              value={form.confirm}
              onChange={(e) => setForm((f) => ({ ...f, confirm: e.target.value }))}
              autoComplete="new-password"
            />
          </label>
        </div>

        {error ? <p className="mt-4 text-sm font-medium text-error">{error}</p> : null}
        {toast ? <p className="mt-4 text-sm font-medium text-tertiary-fixed-dim">{toast}</p> : null}

        <div className="mt-6 flex w-full justify-start">
          <button
            type="button"
            disabled={isSaving}
            onClick={() => void handleSubmit()}
            className="rounded-lg bg-primary px-5 py-2.5 text-sm font-black text-white transition hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSaving ? 'Enregistrement…' : 'Enregistrer mon mot de passe'}
          </button>
        </div>
      </article>
    </FormateurPageShell>
  );
}
